if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          container: ".m-main-product--wrapper",
          slider: ".m-media-gallery__inner",
          sliderPagination: ".swiper-pagination",
          sliderPrevEl: ".swiper-button-prev",
          sliderNextEl: ".swiper-button-next",
          navSlider: ".nav-swiper-container",
          medias: [".m-product-media--item"],
          mediaWrapper: ".m-product-media--wrapper",
          mediaZoomIns: [".m-product-media__zoom-in"],
          videos: [".media-video"],
          featuredImage: ".m-product-media",
          galleryList: ".m-media-gallery__list",
          galleryInner: ".m-media-gallery__inner",
        };

        this.navSlider = false;
        this.slider = false;
        this.slideAccessibility = new Map();
        this.onMediaLayoutChange = this.updateMediaLayout.bind(this);
      }

      get context() {
        return this.dataset.context;
      }

      get layout() {
        return this.dataset.layout;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.mediaQuery?.removeEventListener("change", this.onMediaLayoutChange);
        cancelAnimationFrame(this.mediaResizeFrame);
        this.removeMediaZoomDelegate?.();
        this.removeThumbnailNavigation?.();
        this.lightbox?.destroy();
        if (this.domNodes?.slider && this.domNodes?.galleryList) this.destroySlider();
        this.restoreSlideAccessibility();
      }

      init() {
        if (!this.isConnected) return;
        this.section = this.closest('product-info');
        this.domNodes = queryDomNodes(this.selectors, this);
        this.enableVideoAutoplay = this.dataset.enableVideoAutoplay === "true";
        this.enableImageZoom = this.dataset.enableImageZoom === "true";
        this.enableVariantGroupImages = this.dataset.enableVariantGroupImages === "true";
        this.onlyMedia = this.dataset.onlyMedia === "true";
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.variantPicker = this.section.querySelector("variant-picker");

        this.setSliderOptions();

        this.mediaQuery?.removeEventListener("change", this.onMediaLayoutChange);
        this.mediaQuery = window.matchMedia(MinimogTheme.config.mediaQueryMobile);
        this.mediaQuery.addEventListener("change", this.onMediaLayoutChange);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.handlePhotoswipe();
        };

        this.handleAutoplayVideo();

        this.removeAttribute("data-media-loading");
        this.domNodes.galleryInner.style.opacity = 1;
      }

      updateMediaLayout() {
        if (!this.isConnected) return;
        if (MinimogTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      setSliderOptions() {
        const { sliderPagination, sliderNextEl: nextEl, sliderPrevEl: prevEl } = this.domNodes;

        this.sliderOptions = {
          autoHeight: true,
          navigation: { nextEl, prevEl },
          threshold: 2,
          loop: !this.enableVariantGroupImages && !this.onlyMedia,
          watchSlidesProgress: true,
          watchSlidesVisibility: true,
          pagination: {
            el: sliderPagination,
            clickable: true,
            type: "bullets",
            bulletElement: "button",
            renderBullet: (index, className) => {
              const button = document.createElement("button");
              button.type = "button";
              button.className = className;
              button.setAttribute("aria-label", `${this.dataset.imageLabel} ${index + 1}`);
              return button.outerHTML;
            },
          },
          on: {
            slideChange: (swiper) => this.syncThumbnailCurrent(swiper),
            progress: (swiper) => this.syncSlideAccessibility(swiper),
            loopFix: (swiper) => this.syncSlideAccessibility(swiper),
            update: (swiper) => this.syncSlideAccessibility(swiper),
            beforeDestroy: (swiper) => this.restoreSlideAccessibility(swiper),
            paginationUpdate: (swiper) => {
              Array.from(swiper.pagination.bullets || []).forEach((bullet) => {
                if (bullet.classList.contains(swiper.params.pagination.bulletActiveClass)) bullet.setAttribute("aria-current", "true");
                else bullet.removeAttribute("aria-current");
              });
            },
            init: (swiper) => {
              this.domNodes.slider.style.opacity = 1;
              this.domNodes = queryDomNodes(this.selectors, this.section);
              this.syncSlideAccessibility(swiper);
              cancelAnimationFrame(this.mediaResizeFrame);
              this.mediaResizeFrame = requestAnimationFrame(() => {
                if (this.isConnected && !swiper.destroyed && this.slider === swiper) window.dispatchEvent(new Event("resize"));
              });
            }
          }
        };

        this.thumbsOptions = {
          loop: false,
          slidesPerView: 5,
          freeMode: true,
          spaceBetween: 10,
          threshold: 2,
          watchSlidesVisibility: true,
          watchSlidesProgress: true,
          on: {
            init: () => {
              this.domNodes.navSlider.style.opacity = 1
            }
          }
        };

        switch (this.layout) {
          case 'layout-4':
            break;
          case 'layout-5':
            this.sliderOptions = {
              ...this.sliderOptions,
              watchSlidesProgress: true,
              watchSlidesVisibility: true,
              breakpoints: {
                768: {
                  slidesPerView: 2,
                  slidesPerGroup: 1,
                  spaceBetween: 10,
                },
              }
            }
            break;
          case 'layout-6':
            this.thumbsOptions = {
              ...this.thumbsOptions,
              breakpoints: {
                1024: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                  spaceBetween: 5,
                }
              }
            }
            break;
          case 'layout-7':
            this.sliderOptions = {
              ...this.sliderOptions,
              speed: 500,
              centeredSlides: true,
              breakpoints: {
                768: {
                  slidesPerView: 3
                }
              }
            }
        }
      }

      initSlider() {
        if (typeof this.slider !== 'object') {
          const { slider, navSlider, galleryList } = this.domNodes;

          // Initialize navigation slider if navSlider exists
          this.navSlider = navSlider ? new MinimogLibs.Swiper(navSlider, this.thumbsOptions) : false;

          if (navSlider) {
            this.sliderOptions.thumbs = {
              swiper: this.navSlider
            };
          }

          // Initialize the main slider
          slider.classList.add('swiper-container');
          galleryList.classList.remove('m:grid');
          galleryList.classList.add('swiper-wrapper');

          this.slider = new MinimogLibs.Swiper(slider, this.sliderOptions);

          this.bindThumbnailNavigation();

          // Handle slide change for variant group images
          if (!this.enableVariantGroupImages) this.handleSlideChange();
        }
        this.syncSlideAccessibility(this.slider);
      }

      destroySlider() {
        const { slider, galleryList } = this.domNodes;
        cancelAnimationFrame(this.mediaResizeFrame);
        this.removeThumbnailNavigation?.();
        this.restoreSlideAccessibility();

        slider.classList.remove('swiper-container');
        galleryList.classList.add('m:grid');
        galleryList.classList.remove('swiper-wrapper');

        if (typeof this.slider === 'object') {
          this.slider.destroy();
          this.slider = false;
        }
        if (this.navSlider && typeof this.navSlider === "object") this.navSlider.destroy();
        this.navSlider = false;
      }

      bindThumbnailNavigation() {
        this.removeThumbnailNavigation?.();
        const swiper = this.slider, nav = this.navSlider, element = nav?.el;
        if (!swiper || !nav || !element) return;
        const owner = { swiper, nav };
        this.thumbnailNavigationOwner = owner;
        const current = () => this.isConnected && this.slider === swiper && this.navSlider === nav &&
          this.thumbnailNavigationOwner === owner && !swiper.destroyed && !nav.destroyed && this.contains(element) && element === this.querySelector(this.selectors.navSlider);
        const thumbnail = (event) => {
          if (!current()) return null;
          const button = event.target.closest("button.swiper-slide[data-media-id]");
          if (!button || button.disabled || button.closest(".nav-swiper-container") !== element ||
            !Array.from(nav.slides || []).includes(button) || button.closest("[inert], [aria-hidden='true']")) return null;
          const viewport = element.getBoundingClientRect(), rect = button.getBoundingClientRect();
          return viewport.width > 0 && viewport.height > 0 && rect.width > 0 && rect.height > 0 &&
            getComputedStyle(button).visibility !== "hidden" && getComputedStyle(element).visibility !== "hidden" ? button : null;
        };
        const click = (event) => {
          if (event.detail !== 0 || event.defaultPrevented || !thumbnail(event)) return;
          event.preventDefault();
          nav.updateClickedSlide(event);
          swiper.thumbs.onThumbClick();
        };
        const focus = (event) => {
          const button = thumbnail(event);
          if (!button) return;
          const viewport = element.getBoundingClientRect(), rect = button.getBoundingClientRect();
          const outside = nav.isHorizontal() ? rect.left < viewport.left - 1 || rect.right > viewport.right + 1 :
            rect.top < viewport.top - 1 || rect.bottom > viewport.bottom + 1;
          if (outside) nav.slideTo(Array.from(nav.slides).indexOf(button));
        };
        element.addEventListener("click", click);
        element.addEventListener("focusin", focus);
        this.removeThumbnailNavigation = () => {
          element.removeEventListener("click", click);
          element.removeEventListener("focusin", focus);
          if (this.thumbnailNavigationOwner === owner) {
            this.thumbnailNavigationOwner = null;
            Array.from(nav.slides || []).forEach((button) => button.removeAttribute("aria-current"));
          }
        };
      }

      syncThumbnailCurrent(swiper) {
        const nav = this.navSlider;
        if (!this.isConnected || !swiper || swiper.destroyed || this.slider !== swiper || !nav || nav.destroyed ||
          swiper.el !== this.querySelector(this.selectors.slider) || nav.el !== this.querySelector(this.selectors.navSlider)) return;
        const mediaId = swiper.slides[swiper.activeIndex]?.dataset.mediaId;
        let current = false;
        Array.from(nav.slides || []).forEach((button, index) => {
          const label = `${this.dataset.imageLabel} ${index + 1}`;
          if (button.getAttribute("aria-label") !== label) button.setAttribute("aria-label", label);
          if (!current && mediaId && button.dataset.mediaId === mediaId) {
            if (button.getAttribute("aria-current") !== "true") button.setAttribute("aria-current", "true");
            current = true;
          } else button.removeAttribute("aria-current");
        });
      }

      restoreSlideAttributes(slide, state) {
        ["inert", "aria-hidden"].forEach((name) => {
          if (state[name] === null) slide.removeAttribute(name);
          else slide.setAttribute(name, state[name]);
        });
      }

      restoreSlideAccessibility(swiper) {
        if (swiper && this.slideAccessibilityOwner !== swiper) return;
        this.slideAccessibility.forEach((state, slide) => this.restoreSlideAttributes(slide, state));
        this.slideAccessibility.clear();
        this.slideAccessibilityOwner = null;
      }

      focusVisibleSlide(visible, source, swiper) {
        const sourceSlide = source?.closest(this.selectors.medias[0]);
        const targetSlide = visible.find((slide) => slide.dataset.mediaId === sourceSlide?.dataset.mediaId)
          || visible.find((slide) => slide === swiper.slides[swiper.activeIndex]) || visible[0];
        const controls = targetSlide ? getFocusableElements(targetSlide).filter((node) =>
          !node.closest("[inert], [aria-hidden='true']") && node.getClientRects().length && getComputedStyle(node).visibility !== "hidden"
        ) : [];
        const zoom = source?.closest(this.selectors.mediaZoomIns[0]);
        const target = (zoom && controls.find((node) => node.matches(this.selectors.mediaZoomIns[0]))) || controls[0];
        if (target) target.focus({ preventScroll: true });
        else {
          const tabindex = swiper.el.getAttribute("tabindex");
          swiper.el.setAttribute("tabindex", "-1");
          swiper.el.focus({ preventScroll: true });
          if (tabindex === null) swiper.el.removeAttribute("tabindex");
          else swiper.el.setAttribute("tabindex", tabindex);
        }
      }

      syncSlideAccessibility(swiper) {
        if (!this.isConnected || !swiper || swiper.destroyed || swiper.el !== this.querySelector(this.selectors.slider)) return;
        if (this.slideAccessibilityOwner && this.slideAccessibilityOwner !== swiper) return;
        this.slideAccessibilityOwner = swiper;
        this.syncThumbnailCurrent(swiper);
        const slides = Array.from(swiper.slides || []);
        this.slideAccessibility.forEach((state, slide) => {
          if (!slides.includes(slide)) {
            this.restoreSlideAttributes(slide, state);
            this.slideAccessibility.delete(slide);
          }
        });
        const remember = (slide) => {
          if (!this.slideAccessibility.has(slide)) this.slideAccessibility.set(slide, {
            inert: slide.getAttribute("inert"), "aria-hidden": slide.getAttribute("aria-hidden"),
          });
        };
        slides.filter((slide) => !slide.classList.contains("swiper-slide-duplicate")).forEach(remember);
        slides.forEach((slide) => {
          if (!this.slideAccessibility.has(slide)) {
            const original = slides.find((item) => !item.classList.contains("swiper-slide-duplicate") && item.dataset.mediaId === slide.dataset.mediaId);
            if (original) this.slideAccessibility.set(slide, { ...this.slideAccessibility.get(original) });
            else remember(slide);
          }
        });
        let visible = Array.from(swiper.visibleSlides || []).filter((slide) => slides.includes(slide));
        if (!visible.length && slides[swiper.activeIndex]) visible = [slides[swiper.activeIndex]];
        visible.forEach((slide) => this.restoreSlideAttributes(slide, this.slideAccessibility.get(slide)));
        const hiddenFocus = slides.find((slide) => !visible.includes(slide) && slide.contains(document.activeElement));
        if (hiddenFocus) this.focusVisibleSlide(visible, document.activeElement, swiper);
        slides.filter((slide) => !visible.includes(slide)).forEach((slide) => {
          slide.setAttribute("inert", "");
          slide.setAttribute("aria-hidden", "true");
        });
      }

      handleSlideChange() {
        if (!this.slider) return;

        this.slider.on("slideChange", (swiper) => {
          window.pauseAllMedia(this);

          const { slides, activeIndex } = swiper;
          const activeSlide = slides[activeIndex];
          if (activeSlide) this.playActiveMedia(activeSlide);

          // Determine visible slides based on layout
          const visibleSlides = [activeIndex];
          if (["layout-5", "layout-7"].includes(this.layout)) {
            visibleSlides.push(activeIndex + 1);
          }

          // Check if any of the visible slides have a model media type
          const hasModelMediaType = visibleSlides.some((index) => {
            const slide = slides[index];
            return slide && slide.dataset.mediaType === "model";
          });

          // Toggle slider's draggable state based on media type
          this.toggleSliderDraggableState(!hasModelMediaType);
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.slider.allowTouchMove !== isDraggable) {
          this.slider.allowTouchMove = isDraggable;
        }
      }

      handleAutoplayVideo() {
        const playVideo = (mediaElement) => {
          const mediaType = mediaElement.dataset.mediaType;
          if (mediaType === "video" || mediaType === "external_video") {
            const deferredMedia = mediaElement.querySelector("deferred-media");
            const autoplay = deferredMedia && deferredMedia.dataset.autoPlay === "true";
            if (autoplay) deferredMedia.loadContent(false);
          }
        };

        if (this.slider) {
          const { slides, activeIndex } = this.slider;
          const activeSlide = slides[activeIndex];
          if (activeSlide) playVideo(activeSlide);
        } else {
          this.domNodes.medias.forEach(playVideo);
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector("deferred-media");
        if (!deferredMedia || deferredMedia.dataset.autoPlay !== "true") return;

        const playMedia = (element) => {
          if (element.classList.contains("js-youtube") || element.classList.contains("js-vimeo")) {
            const platform = element.classList.contains("js-youtube") ? "youtube" : "vimeo";
            const param = platform === "youtube" ? "mute=1" : "muted=1";
            const symbol = element.src.includes("?") ? "&" : "?";
            element.src += `${symbol}autoplay=1&${param}`;
          } else {
            element.play();
          }
        };

        if (!deferredMedia.hasAttribute("loaded")) {
          deferredMedia.loadContent(false);
        }

        const deferredElement = deferredMedia.querySelector("video, model-viewer, iframe");
        playMedia(deferredElement);
      }

      handlePhotoswipe() {
        if (!this.isConnected) return;
        this.removeMediaZoomDelegate?.();
        this.lightbox?.destroy();
        const medias = [...this.querySelectorAll(".m-product-media--item:not(.swiper-slide-duplicate)")];

        const data = medias.map((media) => {
          const { mediaType, index: id } = media.dataset;

          if (mediaType === "image") {
            const {
              mediaSrc: src,
              mediaWidth,
              mediaHeight,
              mediaAlt: alt,
            } = media.querySelector(".m-product-media").dataset;
            return {
              src,
              width: parseInt(mediaWidth),
              height: parseInt(mediaHeight),
              alt,
              id,
            };
          } else {
            return {
              html: `<div class="pswp__${mediaType}">${media.innerHTML}</div>`,
              type: mediaType,
              id,
            };
          }
        });

        const options = {
          dataSource: data,
          bgOpacity: 1,
          close: false,
          zoom: false,
          arrowNext: false,
          arrowPrev: false,
          counter: false,
          preloader: false,
          pswpModule: MinimogLibs.PhotoSwipeLightbox.PhotoSwipe,
        };
        this.lightbox = new MinimogLibs.PhotoSwipeLightbox(options);
        const lightbox = this.lightbox;
        lightbox.on("keydown", (event) => {
          const original = event.originalEvent;
          if (!original || event.defaultPrevented || original.defaultPrevented || (original.key !== "Tab" && original.keyCode !== 9) || original.ctrlKey || original.altKey || original.metaKey) return;
          const modal = lightbox.pswp?.element;
          if (!this.isConnected || this.lightbox !== lightbox || lightbox.pswp?.isDestroying || !modal?.isConnected) return;
          const active = document.activeElement;
          if (active !== modal && !modal.contains(active)) return;
          const controls = getFocusableElements(modal).filter((node) =>
            !node.disabled && node.tabIndex >= 0 && !node.closest("[inert], [aria-hidden='true']") && node.getClientRects().length && getComputedStyle(node).visibility !== "hidden"
          );
          const first = controls[0], last = controls[controls.length - 1];
          let target;
          if (!first) target = modal;
          else if (original.shiftKey && (active === modal || active === first)) target = last;
          else if (!original.shiftKey && (active === modal || active === last)) target = first;
          if (!target) return;
          original.preventDefault();
          event.preventDefault();
          target.focus({ preventScroll: true });
        });
        lightbox.on("closingAnimationEnd", () => {
          const opener = lightbox.galleryReturnFocus;
          const modal = lightbox.pswp?.element;
          if (!this.isConnected || this.lightbox !== lightbox || !this.slider || !opener?.isConnected || !this.contains(opener) || !opener.closest("[inert]")) return;
          if (document.activeElement !== document.body && !modal?.contains(document.activeElement)) return;
          const visible = Array.from(this.slider.slides || []).filter((slide) => !slide.inert && slide.getAttribute("aria-hidden") !== "true");
          this.focusVisibleSlide(visible, opener, this.slider);
        });

        this.lightbox.addFilter("thumbEl", (thumbEl, { id }, index) => {
          return this.querySelector(`[data-index="${id}"]:not(.swiper-slide-duplicate) img`) || thumbEl;
        });

        this.lightbox.addFilter("placeholderSrc", (placeholderSrc, { data: { id } }) => {
          const el = this.querySelector(`[data-index="${id}"]:not(.swiper-slide-duplicate) img`);
          return el ? el.src : placeholderSrc;
        });

        this.lightbox.on("change", () => {
          window.pauseAllMedia(this);
          if (this.slider) {
            const { currIndex } = this.lightbox.pswp;
            const slideMethod = this.enableVariantGroupImages ? "slideTo" : "slideToLoop";
            this.slider[slideMethod](currIndex, 100, false);
          }
        });

        this.lightbox.on("pointerDown", (e) => {
          if (this.lightbox.pswp.currSlide.data.type === "model") {
            e.preventDefault();
          }
        });

        // UI elements
        const uiElements = [
          {
            name: "m-close",
            title: this.dataset.closeLabel,
            order: 11,
            isButton: true,
            html: '<svg class="m-svg-icon--large" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>',
            onClick: (event, el, pswp) => pswp.close(),
          },
          {
            name: "m-arrowNext",
            title: this.dataset.nextLabel,
            order: 12,
            isButton: true,
            html: '<svg fill="currentColor" width="14px" height="14px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M218.101 38.101L198.302 57.9c-4.686 4.686-4.686 12.284 0 16.971L353.432 230H12c-6.627 0-12 5.373-12 12v28c0 6.627 5.373 12 12 12h341.432l-155.13 155.13c-4.686 4.686-4.686 12.284 0 16.971l19.799 19.799c4.686 4.686 12.284 4.686 16.971 0l209.414-209.414c4.686-4.686 4.686-12.284 0-16.971L235.071 38.101c-4.686-4.687-12.284-4.687-16.97 0z"></path></svg>',
            onClick: (event, el, pswp) => pswp.next(),
          },
          {
            name: "m-arrowPrev",
            title: this.dataset.previousLabel,
            order: 10,
            isButton: true,
            html: '<svg width="14px" height="14px" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M229.9 473.899l19.799-19.799c4.686-4.686 4.686-12.284 0-16.971L94.569 282H436c6.627 0 12-5.373 12-12v-28c0-6.627-5.373-12-12-12H94.569l155.13-155.13c4.686-4.686 4.686-12.284 0-16.971L229.9 38.101c-4.686-4.686-12.284-4.686-16.971 0L3.515 247.515c-4.686 4.686-4.686 12.284 0 16.971L212.929 473.9c4.686 4.686 12.284 4.686 16.971-.001z"></path></svg>',
            onClick: (event, el, pswp) => pswp.prev(),
          },
        ];

        this.lightbox.on("uiRegister", () => {
          this.lightbox.pswp.element.setAttribute("aria-label", this.dataset.galleryLabel);
          this.lightbox.pswp.element.setAttribute("aria-modal", "true");
          this.lightbox.pswp.ui.registerElement(uiElements[0]);
          if (!this.onlyMedia) {
            uiElements.slice(1).forEach((element) => this.lightbox.pswp.ui.registerElement(element));
          }
        });

        this.lightbox.init();

        const handleMediaClick = (e, media) => {
          e.preventDefault();
          const isImage = media.classList.contains("media-type-image");
          const isZoomButton = e.target.closest(this.selectors.mediaZoomIns[0]);

          if (isImage || isZoomButton) {
            const index = Number(media.dataset.index) || 0;
            this.lightbox.galleryReturnFocus = document.activeElement;
            this.lightbox.loadAndOpen(index);
          }
        };

        this.removeMediaZoomDelegate = addEventDelegate({
          selector: this.selectors.medias[0],
          context: this,
          handler: handleMediaClick,
        });
      }

      setActiveMedia(variant) {
        if (!variant) return;
        if (this.slider) {
          this.setActiveMediaForSlider(variant);
        } else {
          this.handleGalleryMode(variant);
        }
      }

      setActiveMediaForSlider(variant) {
        if (variant.featured_media && this.slider && this.slider.wrapperEl) {
          let slideIndex = variant.featured_media.position || 1;
          this.slider.slideToLoop(slideIndex - 1);
        }
      }

      handleGalleryMode(variant) {
        if (this.context !== "featured-product" && variant && variant.featured_media) {
          const selectedMedia = this.querySelector(`.m-media-gallery__list [data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            this.scrollIntoView(selectedMedia);
          }
        }
      }

      scrollIntoView(selectedMedia) {
        selectedMedia.scrollIntoView({
          behavior: "smooth",
        });
      }
    }
  );
}
